import React, { useEffect, useRef, useState, useMemo } from "react";

const ProductImageViewer = ({
  images = [],
  zoomScale = 2.5,
  width = 300,
  height = 270,
  slideInterval = 3000,
}) => {
  const normalizedImages = useMemo(
    () =>
      images.map((img) => ({
        url: img?.URL || img?.src || img?.url || "/images/not-available.png",
      })),
    [images]
  );

  const [selectedImage, setSelectedImage] = useState(
    normalizedImages?.[0]?.url || ""
  );
  const [isMobile, setIsMobile] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [backgroundPosition, setBackgroundPosition] = useState("0% 0%");
  const [isHovering, setIsHovering] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const imgRef = useRef(null);
  const touchStartX = useRef(0);

  const currentImage = normalizedImages[currentIndex]?.url || "";

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto-slide for desktop
  useEffect(() => {
    if (!isMobile) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % normalizedImages.length);
      }, slideInterval);
      return () => clearInterval(interval);
    }
  }, [normalizedImages.length, slideInterval, isMobile]);

  // Zoom positioning
  const handleMouseMove = (e) => {
    const { left, top, width, height } = imgRef.current.getBoundingClientRect();
    const x = ((e.pageX - left - window.scrollX) / width) * 100;
    const y = ((e.pageY - top - window.scrollY) / height) * 100;
    setBackgroundPosition(`${x}% ${y}%`);
  };

  // Mobile swipe handlers
  const handleTouchStart = (e) => (touchStartX.current = e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (deltaX > 50) goToPrevImage();
    else if (deltaX < -50) goToNextImage();
  };

  const goToNextImage = () => {
    const next = (selectedImageIndex + 1) % normalizedImages.length;
    setSelectedImageIndex(next);
  };

  const goToPrevImage = () => {
    const prev =
      (selectedImageIndex - 1 + normalizedImages.length) %
      normalizedImages.length;
    setSelectedImageIndex(prev);
  };

  const openModal = (index) => setSelectedImageIndex(index);
  const closeModal = () => setSelectedImageIndex(null);

  // Zooming in modal
  const handleWheel = (e) => {
    e.preventDefault();
    const img = imgRef.current;
    const currentScale =
      parseFloat(img.style.transform.replace("scale(", "").replace(")", "")) ||
      1;
    let scale = currentScale + (e.deltaY < 0 ? 0.1 : -0.1);
    scale = Math.max(1, Math.min(3, scale));
    img.style.transform = `scale(${scale})`;
  };

  const handleDrag = () => {
    let isDragging = false;
    let startX, startY;
    let img = imgRef.current;

    const onMouseDown = (e) => {
      isDragging = true;
      startX = e.clientX - img.offsetLeft;
      startY = e.clientY - img.offsetTop;
      img.style.cursor = "grabbing";
      img.style.position = "absolute";
    };

    const onMouseMove = (e) => {
      if (!isDragging) return;
      img.style.left = `${e.clientX - startX}px`;
      img.style.top = `${e.clientY - startY}px`;
    };

    const onMouseUp = () => {
      isDragging = false;
      img.style.cursor = "grab";
    };

    img.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      img.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  };

  useEffect(() => {
    if (selectedImageIndex !== null) {
      const cleanup = handleDrag();
      return cleanup;
    }
  }, [selectedImageIndex]);

  const handleImageClick = (url, index) => {
    setSelectedImage(url);
    setCurrentIndex(index);
  };

  return (
    <>
      {/* DESKTOP: Image Zoomer */}
      {!isMobile && (
        <div
          style={{
            position: "relative",
            // display: "inline-block",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            ref={imgRef}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            style={{
              width,
              height,
              backgroundImage: `url(${currentImage})`,
              backgroundSize: "contain",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              cursor: "zoom-in",
              transition: "background-image 0.5s ease-in-out",
            }}
          />

          {isHovering && (
            <div
              style={{
                position: "absolute",
                top: "-40px",
                left: `calc(${width}px + 40px)`,
                width: "650px",
                height: "450px",
                backgroundImage: `url(${currentImage})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: `${zoomScale * 100}%`,
                backgroundPosition,
                zIndex: 999,
                backgroundColor: "#fff",
                transition: "background-image 0.5s ease-in-out",
                boxShadow: "0 0 4px 0 rgba(0, 0, 0, 0.251)",
              }}
            />
          )}
        </div>
      )}

      {/* MOBILE: Carousel */}
      {isMobile && (
        <div style={{ position: "relative", overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              transform: `translateX(-${currentIndex * 100}%)`,
              transition: "transform 0.5s ease",
            }}
          >
            {normalizedImages.map((img, index) => (
              <img
                key={index}
                src={img.url}
                alt={`slide-${index}`}
                style={{
                  width: "100%",
                  flexShrink: 0,
                  objectFit: "contain",
                }}
                onClick={() => openModal(index)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Modal on Mobile Click */}
      {isMobile && selectedImageIndex !== null && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.9)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={closeModal}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button
            style={{
              position: "absolute",
              top: 10,
              right: 20,
              fontSize: 50,
              border: "none",
              width: 30,
              color: "white",
              background: "transparent",
            }}
            onClick={closeModal}
          >
            ×
          </button>
          <button
            style={{ position: "absolute", left: 10 }}
            onClick={(e) => {
              e.stopPropagation();
              goToPrevImage();
            }}
          >
            ❮
          </button>
          <button
            style={{ position: "absolute", right: 10 }}
            onClick={(e) => {
              e.stopPropagation();
              goToNextImage();
            }}
          >
            ❯
          </button>
          <img
            ref={imgRef}
            src={normalizedImages[selectedImageIndex].url}
            alt="zoom"
            onWheel={handleWheel}
            draggable={false}
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              transform: "scale(1)",
              cursor: "grab",
              position: "relative",
            }}
          />
        </div>
      )}

      {/* Thumbnails */}
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          justifyContent: "center",
          flexWrap: "wrap",
          marginTop: "1rem",
        }}
      >
        {normalizedImages.map((item, i) => (
          <div
            key={i}
            onClick={() => handleImageClick(item.url, i)}
            style={{
              border:
                currentIndex === i
                  ? "2px solid #4a2a7d"
                  : "1px solid lightgray",
              padding: "2px",
              borderRadius: "4px",
              cursor: "pointer",
              width: "60px",
              height: "60px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
            }}
          >
            <img
              src={item.url}
              alt="thumb"
              style={{ maxWidth: "100%", maxHeight: "100%" }}
            />
          </div>
        ))}
      </div>
    </>
  );
};

export default ProductImageViewer;
